import { observer } from "mobx-react-lite";
import type {
  ActionOutputData,
  OutputData,
  ValueData,
  RecordJobData,
} from "@smartsuite/automation-engine-api";
import { t } from "i18next";
import { valueDataToStringsWithDelimenters } from "@smartsuite/automation-conditions";
import { translateAction } from "../../../../../../../locales/i18n";
import { ActionError } from "../../../ActionError/ActionError";
import { ActivityDetailsTriggerGroup } from "../../../ActivityDetailsTriggerGroup/ActivityDetailsTriggerGroup";
import { ActivityDetailsItem } from "../../../ActivityDetailsItem/ActivityDetailsItem";
import {
  addEmptyValueIfNeeded,
  getErrorMessage,
} from "../../../../../ActivityGrid/ActivityGrid.helpers";
import { ActivityDetailsGroup } from "../../../ActivityDetailsGroup/ActivityDetailsGroup";
import { getEditRecordUrl } from "../../../../utils";
import { useActionDetails } from "../../hooks/useActionDetails";
import { Fragment, useMemo } from "react";
import type { ReactNode } from "react";
import { useRootStore } from "../../../../../../../stores/hooks";

interface MergeLoopingRecordsGroupProps {
  action: ActionOutputData;
  jobId: string;
  groupInstanceId: number;
  recordListCount: number | undefined;
  onDetailsClick: (recordId: string) => void;
  recordsJobData: Record<string, RecordJobData>;
}

const recordOutputIds = [
  "field-id",
  "record_id",
  "application_id",
  "form_name",
  "form_url",
  "title",
];

export const MergeLoopingRecordsGroup = observer<MergeLoopingRecordsGroupProps>(
  ({
    action,
    jobId,
    groupInstanceId,
    recordListCount,
    onDetailsClick,
    recordsJobData,
  }) => {
    const { selectedAction } = useActionDetails(action);
    const { jobDetails } = useRootStore();
    const errorMessage = getErrorMessage(action, jobDetails.job);
    const status = action.status.oneofKind === "ok" ? "is_success" : "is_error";
    const recordJobBaseUrl = `jobs/${jobId}/group-${groupInstanceId}/action-${action.actionReference?.instanceId}/record-`;
    // The engine reports a merge's last record job index here, not a count:
    // one that merged pairwise has one job per pair, one that merged in a
    // single call reports 0 for its one job.
    const recordJobCount = Math.max(recordListCount ?? 0, 1);

    const automationActionUsageArray: RecordJobData[] = useMemo(() => {
      const result: RecordJobData[] = [];

      for (let i = 0; i < recordJobCount; i++) {
        const recordJobId = `${recordJobBaseUrl}${i}`;
        result.push(recordsJobData[recordJobId]);
      }

      return result;
    }, [
      recordJobCount,
      recordJobBaseUrl,
      jobId,
      action.actionReference?.instanceId,
      recordsJobData,
    ]);

    if (!action.actionReference) {
      return null;
    }

    return (
      <ActivityDetailsGroup
        label={
          selectedAction !== undefined ? translateAction(selectedAction) : ""
        }
        subLabel={t("job-list.records-found", {
          count: recordListCount,
        })}
        status={status}
      >
        {status === "is_error" && <ActionError errorMessage={errorMessage} />}

        {automationActionUsageArray.map((recordJob, index) => {
          const outputs =
            recordJob?.outputData !== undefined
              ? getRecordListOutputs(recordJob.outputData)
              : [];

          // do not show entire fields
          const withoutEnterFields =
            filterEntireFieldsFromRecordJobData(outputs);

          const filteredOutputs = withoutEnterFields.filter(
            (output) => !recordOutputIds.includes(output.outputId),
          );

          const mergedAndDeletedRecords =
            recordJob?.outputData !== undefined
              ? getMergedRecordIds(recordJob.outputData, outputs)
              : [];
          const applicationId = findOutputValueAndLabel(
            "application_id",
            outputs,
          );
          const solutionId = findOutputValueAndLabel("solution_id", outputs);
          const mergedToRecord = findOutputValueAndLabel("record_id", outputs);
          const fieldsToDisplay =
            recordJob !== undefined ? getMergedFields(recordJob) : [];

          return (
            <ActivityDetailsTriggerGroup
              key={index}
              title={`Merge Result ${index + 1}`}
              onClick={() => {
                onDetailsClick(`${recordJobBaseUrl}${index}`);
              }}
            >
              <ActivityDetailsItem label={"Merged Record Id (Deleted Record)"}>
                {mergedAndDeletedRecords.map((recordId, recordIndex) => (
                  <Fragment key={recordId}>
                    {recordIndex > 0 && ", "}
                    {getLinkToRecord(recordId, solutionId, applicationId)}
                  </Fragment>
                ))}
              </ActivityDetailsItem>
              <ActivityDetailsItem label={"Merged To Record Id"}>
                {getLinkToRecord(mergedToRecord, solutionId, applicationId)}
              </ActivityDetailsItem>
              {filteredOutputs
                .filter((output) => output.outputId !== "merged-record-id")
                .filter((output) =>
                  fieldsToDisplay.includes(
                    output.outputId.replace("field-", "").split(".")[0],
                  ),
                )
                .map((output) => {
                  return (
                    <ActivityDetailsItem
                      key={output.outputId}
                      label={output.outputLabel}
                    >
                      {addEmptyValueIfNeeded(getOutputValue(output.value))}
                    </ActivityDetailsItem>
                  );
                })}
            </ActivityDetailsTriggerGroup>
          );
        })}
      </ActivityDetailsGroup>
    );
  },
);

const findOutputValueAndLabel = (
  outputId: string,
  outputs: OutputData[],
): string | undefined => {
  const output = outputs.find((output) => output.outputId === outputId);
  if (output === undefined) {
    return undefined;
  }

  return getOutputValue(output.value);
};

// A merge that ran once reports each record it merged as a run; one that ran
// once per pair carries the single merged record on the record itself.
const getMergedRecordIds = (
  outputData: ActionOutputData,
  outputs: OutputData[],
): string[] => {
  const fromRuns = (outputData.runs ?? [])
    .map((run) => findOutputValueAndLabel("merged-record-id", run.outputs))
    .filter((recordId): recordId is string => recordId !== undefined);
  if (fromRuns.length > 0) {
    return fromRuns;
  }
  const fromRecord = findOutputValueAndLabel("merged-record-id", outputs);
  return fromRecord === undefined ? [] : [fromRecord];
};

const getRecordListOutputs = (outputData: ActionOutputData): OutputData[] => {
  if (outputData.recordList?.recordList === undefined) {
    return [];
  }
  return outputData.recordList?.recordList?.records[0]?.outputs;
};

const getOutputValue = (value: ValueData | undefined): string => {
  return valueDataToStringsWithDelimenters(value).join("; ");
};

const getLinkToRecord = (
  recordId: string | undefined,
  solutionId?: string,
  applicationId?: string,
): ReactNode => {
  if (recordId === undefined) {
    return <></>;
  }
  return (
    <a
      href={getEditRecordUrl(recordId, solutionId, applicationId)}
      target="_blank"
      rel="noreferrer"
    >
      {recordId}
    </a>
  );
};

const filterEntireFieldsFromRecordJobData = (
  outputs: OutputData[],
): OutputData[] => {
  return outputs.filter((output) => {
    //   filter the same field
    if (
      outputs.some((output2) => "field-" + output.outputId === output2.outputId)
    ) {
      return false;
    }
    // filter entire field
    if (!isFieldWithSuffix(output)) {
      const isExistWithSuffix = outputs.some((output2) => {
        return (
          output2.outputId.includes(output.outputId) &&
          isFieldWithSuffix(output2)
        );
      });
      return !isExistWithSuffix;
    }
    return true;
  });
};

const isFieldWithSuffix = (input: OutputData): boolean => {
  return input.outputId.includes(".");
};

const getMergedFields = (recordJob: RecordJobData): string[] => {
  const mergedFieldsInput = recordJob.inputs.find(
    (input) => input.inputId === "merge-actions",
  );
  if (mergedFieldsInput === undefined) {
    return [];
  }
  const input = mergedFieldsInput.value?.values.find(
    (item) => item.value.oneofKind === "mergeFieldActions",
  );
  if (input === undefined) {
    return [];
  }
  if (input.value.oneofKind !== "mergeFieldActions") {
    return [];
  }
  const fields = input.value.mergeFieldActions.fields.map(
    (field) => field.fieldSlug,
  );
  return fields;
};
